import { useEffect, useMemo, useState } from "react";
import { Loader2, Store as StoreIcon } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { useDuplicateCheck } from "@/hooks/useDuplicateCheck";
import { storesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { MARKETPLACES } from "@/lib/constants";

const EMPTY = {
  store_name: "",
  marketplace: "",
  store_id: "",
  client_id: "",
  status: "active",
  assigned_va_agency_id: "none",
  assigned_va_staff_ids: [],
  notes: "",
};

export function StoreDrawer({ open, onOpenChange, record, clients = [], agencies = [], vaStaff = [], onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      setForm(
        record
          ? {
              store_name: record.store_name || "",
              marketplace: record.marketplace || "",
              store_id: record.store_id || "",
              client_id: record.client_id || "",
              status: record.status || "active",
              assigned_va_agency_id: record.assigned_va_agency_id || "none",
              assigned_va_staff_ids: record.assigned_va_staff_ids || [],
              notes: record.notes || "",
            }
          : EMPTY
      );
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const { result: dup } = useDuplicateCheck(
    () =>
      storesService.checkDuplicate({
        marketplace: form.marketplace,
        store_id: form.store_id,
        name: form.store_name,
        exclude_id: record?.id || "",
      }),
    [form.marketplace, form.store_id, form.store_name, open],
    open && (!!form.store_id.trim() || !!form.store_name.trim())
  );

  const keyConflict = dup?.key_conflict;
  const nameWarning = dup?.name_warning;

  const nameError = !form.store_name.trim() ? "Store name is required" : "";
  const marketplaceError = !form.marketplace ? "Marketplace is required" : "";
  const storeIdError = !form.store_id.trim()
    ? "Store ID is required"
    : keyConflict
    ? "This Marketplace + Store ID already exists"
    : "";
  const clientError = !form.client_id ? "A client is required" : "";
  const canSave = !nameError && !marketplaceError && !storeIdError && !clientError && !saving;

  const eligibleStaff = useMemo(() => {
    if (form.assigned_va_agency_id === "none") return [];
    return vaStaff.filter((u) => u.va_agency_id === form.assigned_va_agency_id);
  }, [vaStaff, form.assigned_va_agency_id]);

  const toggleStaff = (id) =>
    setForm((f) => ({
      ...f,
      assigned_va_staff_ids: f.assigned_va_staff_ids.includes(id)
        ? f.assigned_va_staff_ids.filter((x) => x !== id)
        : [...f.assigned_va_staff_ids, id],
    }));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        store_name: form.store_name.trim(),
        marketplace: form.marketplace,
        store_id: form.store_id.trim(),
        client_id: form.client_id,
        status: form.status,
        assigned_va_agency_id:
          form.assigned_va_agency_id === "none" ? null : form.assigned_va_agency_id,
        assigned_va_staff_ids:
          form.assigned_va_agency_id === "none" ? [] : form.assigned_va_staff_ids,
        notes: form.notes.trim(),
      };
      const res = isEdit
        ? await storesService.update(record.id, body)
        : await storesService.create(body);
      toast.success(isEdit ? "Store updated" : "Store created");
      onOpenChange(false);
      onSaved?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={StoreIcon}
      title={isEdit ? "Edit Store" : "New Store"}
      description={isEdit ? "Update store details." : "Connect a store to a client."}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="store-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="store-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create store"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Store details">
          <Field label="Store Name" required error={nameError} warning={!nameError ? (nameWarning ? "A store with this name already exists under another client." : "") : ""}>
            <Input
              value={form.store_name}
              onChange={(e) => set("store_name")(e.target.value)}
              placeholder="Auralux US"
              data-testid="store-name-input"
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Marketplace" required error={marketplaceError}>
              <Select value={form.marketplace} onValueChange={set("marketplace")}>
                <SelectTrigger data-testid="store-marketplace-select"><SelectValue placeholder="Select marketplace" /></SelectTrigger>
                <SelectContent>
                  {MARKETPLACES.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Store ID" required error={storeIdError}>
              <Input
                value={form.store_id}
                onChange={(e) => set("store_id")(e.target.value)}
                placeholder="AUX-001"
                data-testid="store-id-input"
              />
            </Field>
          </div>
          <Field label="Client" required error={clientError}>
            <Select value={form.client_id} onValueChange={set("client_id")}>
              <SelectTrigger data-testid="store-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </DrawerSection>

        <DrawerSection title="Assignment & status">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="store-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assigned VA Agency">
              <Select
                value={form.assigned_va_agency_id}
                onValueChange={(v) => setForm((f) => ({ ...f, assigned_va_agency_id: v, assigned_va_staff_ids: [] }))}
              >
                <SelectTrigger data-testid="store-agency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {agencies.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Assigned VA Staff" hint={form.assigned_va_agency_id === "none" ? "Select an agency to assign staff." : "Staff can be assigned to multiple stores."}>
            {form.assigned_va_agency_id !== "none" && (
              <div className="space-y-2 rounded-lg border border-border p-3">
                {eligibleStaff.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No staff in this agency yet.</p>
                ) : (
                  eligibleStaff.map((u) => (
                    <label key={u.id} className="flex cursor-pointer items-center gap-2.5 text-sm">
                      <Checkbox
                        checked={form.assigned_va_staff_ids.includes(u.id)}
                        onCheckedChange={() => toggleStaff(u.id)}
                        data-testid={`store-staff-${u.id}`}
                      />
                      <span className="text-foreground">{u.name}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </Field>
        </DrawerSection>

        <DrawerSection title="Notes">
          <Field label="Notes">
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              placeholder="Internal notes about this store…"
              rows={3}
              data-testid="store-notes-input"
            />
          </Field>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
